import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { useSupabase } from "@/auth/SupabaseProvider";

import { apiFetch } from "./client";
import type { ModelRow, ModelsResponse } from "./types";

/**
 * TanStack Query hook for the models registry — surfaces the friendly
 * `displayName` keyed by the technical `id` so the SPA can render
 * "Claude Haiku 4.5" in place of `claude-haiku-4-5-20251001` on
 * JobDetail's cost summary and per-call detail panels. The technical
 * id stays available via `title=` hover for forensic lookups (e.g.
 * cross-referencing a `run_log.modelId` cell from the live DB).
 *
 * Returns the raw response array plus a memoised `modelsById` lookup
 * map so consumers do `modelsById[id]?.displayName ?? id` without
 * re-iterating on every render. Mirrors the shape of `useAgents` —
 * the two hooks are the matched pair for the admin-ui display-name
 * convention (M1 #85 step 1 + step 2). Models change rarely (a new
 * Anthropic model release per quarter or so), so the per-session cache
 * managed by QueryClientProvider is plenty.
 *
 * The endpoint is read-only static data on the orchestrator side — no
 * DB hit, no Sheets read — so this hook is cheap and unrate-limited.
 */
export function useModels() {
  const { client } = useSupabase();
  const query = useQuery({
    queryKey: ["models"],
    queryFn: ({ signal }) =>
      apiFetch<ModelsResponse>(client, "/api/models", { signal }),
  });

  const modelsById = useMemo(() => {
    const map: Record<string, ModelRow> = {};
    for (const row of query.data?.models ?? []) {
      map[row.id] = row;
    }
    return map;
  }, [query.data]);

  return { ...query, modelsById };
}
